import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Check, X, Send } from "lucide-react";

export default function QuestionnaireDialog({ open, onOpenChange, topic, onComplete }) {
  const [answers, setAnswers] = useState({});
  if (!topic) return null;

  const setAnswer = (id, val) => setAnswers((a) => ({ ...a, [id]: val }));
  const allAnswered = topic.questions.every((q) => answers[q.id] !== undefined);

  const submit = () => {
    const lines = topic.questions.map((q, i) => `${i + 1}. ${q.text} — Answer: ${answers[q.id] ? "YES" : "NO"}`).join("\n");
    const prompt = `I completed the "${topic.label}" case-assessment questionnaire. Based on my yes/no answers below, evaluate whether I may have a viable ${topic.label.toLowerCase()} matter under the applicable authority, identify the governing legal framework, and explain next steps.\n\n${lines}`;
    onComplete(prompt, topic);
    setAnswers({});
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg rounded-none bg-card border-border" data-testid="questionnaire-dialog">
        <DialogHeader>
          <span className="overline text-primary">Case Assessment</span>
          <DialogTitle className="font-serif-display text-2xl">{topic.label}</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground -mt-1">{topic.intro}</p>
        <div className="space-y-3 max-h-[50vh] overflow-y-auto pr-1 mt-2">
          {topic.questions.map((q, i) => (
            <div key={q.id} className="border border-border/60 bg-background/40 p-3.5">
              <p className="text-sm mb-3 leading-relaxed"><span className="text-primary font-mono-tech mr-1.5">{i + 1}.</span>{q.text}</p>
              <div className="flex gap-2">
                <button onClick={() => setAnswer(q.id, true)} data-testid={`q-${q.id}-yes`}
                        className={`flex-1 flex items-center justify-center gap-1.5 text-xs font-medium py-2 border transition-colors ${answers[q.id] === true ? "bg-[#10B981]/15 border-[#10B981]/50 text-[#10B981]" : "border-border text-muted-foreground hover:border-foreground/40"}`}>
                  <Check className="w-3.5 h-3.5" /> Yes
                </button>
                <button onClick={() => setAnswer(q.id, false)} data-testid={`q-${q.id}-no`}
                        className={`flex-1 flex items-center justify-center gap-1.5 text-xs font-medium py-2 border transition-colors ${answers[q.id] === false ? "bg-destructive/15 border-destructive/50 text-destructive" : "border-border text-muted-foreground hover:border-foreground/40"}`}>
                  <X className="w-3.5 h-3.5" /> No
                </button>
              </div>
            </div>
          ))}
        </div>
        <Button onClick={submit} disabled={!allAnswered} data-testid="questionnaire-submit-btn"
                className="w-full h-11 rounded-none bg-primary text-primary-foreground hover:bg-primary/90 font-semibold disabled:opacity-40">
          <Send className="w-4 h-4 mr-2" /> Ask Leego to assess my case
        </Button>
      </DialogContent>
    </Dialog>
  );
}
